import { useCategoryItemContext } from "../../../../../../services/context/CategoryItemContext";
import { useCustomerAnalytics } from "../../../../../../services/hooks/useCustomerAnalytics";
import { useModalHook } from "../../../../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../../../services/hooks/useTranslationI18";
import {
  setBottomSheetSource,
  setBottomSheetState,
} from "../../../../../../services/redux/slices/globalComponentsToggleSlice";
import { setSelectedItemTimeBoundData } from "../../../../../../services/redux/slices/itemStatusSlice";
import { TransformOfferType } from "../../../../../../services/redux/types/TypeHelper";
import CartItem from "../../../../../../utils/CartItem";
import {
  CUSTOMER_ANALYTICS_EVENT,
  ChangeCounterFrom,
  DISPLAYED_NAME,
} from "../../../../../../utils/enums";
import { cn } from "../../../../../../utils/cn";
import { getItemTagLabels } from "../../../../../../utils/help/itemExtras/itemExtras";
import { getDisplayedName } from "../../../../../../utils/help/localization/localization";
import { isValidOffer } from "../../../../../../utils/help/offers/offers";
import { ItemOfferV1Type, OFFER_TYPE } from "../../../../../../utils/types";
import { CounterV2 } from "../../../../../global/Counter/CounterV2";
import { DiscountedPriceV2 } from "../../../../../global/DiscountedPrice/DiscountedPriceV2";
import { ItemName } from "../../../../../shared/ItemName/ItemName";
import { ItemTagChips } from "../../../../../shared/ItemTagChips/ItemTagChips";
import { PImage } from "../../../../../shared/PImage/PImage";
import { endsWithGif } from "../CategoryItemDetails/CategoryItemDetailsHelper";
import { LabelV2 } from "../Label/LabelV2";
import { useCategoryItemCard } from "../useCategoryItemCard";
import styles from "./CategoryItemCardV2.module.scss";

export const CategoryItemCardV2List = () => {
  const { item, categoryId, index, addItem } = useCategoryItemContext();
  const dispatch = useAppDispatch();
  const addCustomerAnalytics = useCustomerAnalytics();
  const { renderWebViewItem } = useModalHook();
  const { t } = useTranslationI18();

  const {
    itemDetails,
    isOutOfStock,
    cartCountExcludingOffers,
    cartItemIndexExcludingOffers,
    cartItemExcludingOffers,
    shouldOpenBottomSheet,
    canAddToCart,
    hidePrice,
    isMobile,
  } = useCategoryItemCard();

  // Tags sit above the name in list view, which makes the row taller — the
  // thumbnail keeps its own height and stays pinned to the top.
  const hasTag = !isOutOfStock && getItemTagLabels(itemDetails).length > 0;

  const offerGet = t("common:offer-get");
  const offerOff = t("common:offer-off");

  const handleCardClick = () => {
    if (shouldOpenBottomSheet) {
      dispatch(setBottomSheetSource("item_selection"));
      dispatch(setBottomSheetState(true));

      return;
    }

    dispatch(setSelectedItemTimeBoundData(item.timeBoundAvailabilityData));
    !isMobile && renderWebViewItem(itemDetails.id);
  };

  const handleAddClick = (e?: React.MouseEvent) => {
    if (isMobile) {
      e?.preventDefault();
    } else {
      e?.stopPropagation();
    }

    if (!canAddToCart) {
      return;
    }

    if (shouldOpenBottomSheet) {
      addCustomerAnalytics(
        CUSTOMER_ANALYTICS_EVENT.ADD_ITEM_ATTEMPT_BEFORE_BRANCH_AND_ORDER_TYPE_SELECTION
      );
      dispatch(setBottomSheetSource("item_selection"));
      dispatch(setBottomSheetState(true));

      return;
    }

    if (cartCountExcludingOffers > 0) {
      return;
    }

    dispatch(setSelectedItemTimeBoundData(item.timeBoundAvailabilityData));
    addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.ADD_ITEM_FROM_MENU);
    addItem(e, index);
  };

  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const isMainDark = restaurantInfo?.colors?.theme === "main_dark";

  return (
    <div
      onClick={handleCardClick}
      className={cn(styles.list_item, hasTag && styles.has_tag)}
      id={categoryId.toString()}
      key={itemDetails.img_url + index}
    >
      <div className={styles.img_container}>
        <PImage
          id={`next-img-${itemDetails.id}`}
          src={itemDetails.img_url}
          alt={itemDetails.item_name}
          fill
          style={{ objectFit: "cover", objectPosition: "center" }}
          unoptimized={endsWithGif(itemDetails.img_url)}
          quality="100"
          className={styles.img}
          sizes="50vw"
        />
        <LabelV2 />
      </div>

      <div
        className={styles.list_item__text}
        data-testid={`list-item-${itemDetails.item_name}`}
      >
        <div className={styles.list_item__text_content}>
          <ItemTagChips item={itemDetails} isOutOfStock={isOutOfStock} />
          <span
            className={styles.list_item__name}
            data-testid={`list-item-name-${itemDetails.item_name}`}
          >
            <ItemName item={item} />
          </span>
          <span className={styles.list_item__description}>
            {getDisplayedName(itemDetails, DISPLAYED_NAME.DESCRIPTION)}
          </span>
          {isValidOffer(itemDetails?.offer as ItemOfferV1Type) && (
            <p className={styles.list_item__offer_text}>
              {offerGet} {itemDetails.offer?.offerValue}
              {itemDetails.offer?.offerType === OFFER_TYPE.PERCENTAGE
                ? "%"
                : ""}{" "}
              {offerOff}
            </p>
          )}
        </div>
        {!hidePrice && (
          <div className={styles.list_item__price}>
            <DiscountedPriceV2
              item={itemDetails}
              isValidOffer={isValidOffer(itemDetails.offer as ItemOfferV1Type)}
            />
          </div>
        )}
      </div>

      <div
        className={styles.list_item__actions}
        onClick={(e) => {
          handleAddClick(e);
        }}
      >
        <CounterV2
          count={cartCountExcludingOffers}
          itemIndex={cartItemIndexExcludingOffers}
          data={cartItemExcludingOffers as TransformOfferType<CartItem>}
          changeCounterView={ChangeCounterFrom.CartView}
          isDisabled={!canAddToCart}
          size="sm"
          layout="vertical"
          includeBackground={isMainDark}
        />
      </div>
    </div>
  );
};
